/**
 * Every GraphQL document the AI pricing screen sends, built against the frozen
 * contract in `parsina.bff.admin/src/graphql/typeDefs/aiCost.js` field for
 * field. They live in one file so a contract change is a diff against one
 * place, and so a test can match a call by the operation name it carries.
 */

const PRICE_FIELDS = `
  id modelId effectiveFrom
  inputUsdPerM outputUsdPerM cachedInputUsdPerM audioInputUsdPerM audioUsdPerMinute requestUsd
  source note createdAt createdBy
`;

const SERVICE_ROW_FIELDS = `
  serviceKey labelFa labelEn walletServiceType chargeMode chargeCondition
  defaultParseh declaredEstimateUsd active
  invocations chargedRate avgCalls avgInputTokens avgOutputTokens avgCachedTokens
  meanCostUsd p50CostUsd p90CostUsd pricedShare costPerMinuteUsd totalCostUsd
  revenueParseh revenueUsd marginPct meanCostToman
  effectiveParseh priceSource unit declaredInAppsParseh appsMismatch
  models { modelId role calls avgInputTokens avgOutputTokens costUsd shareOfCostPct shareOfCallsPct unpriced }
  hasUnpricedModel declaredRolesMismatch
`;

const DAILY_FIELDS = 'day costUsd revenueUsd invocations calls';

const INVOCATION_FIELDS = `
  requestId serviceKey ownerType ownerId appointmentId startedAt finishedAt status
  calls inputTokens outputTokens cachedTokens audioSeconds costUsd providerCostUsd
  chargedParseh chargeReason priceSource walletTransactionId billedMinutes
`;

const POLICY_FIELDS = `
  id status name fxTomanPerUsd targetMarginPct overheadPct costBasis windowDays rounding
  minParseh note parsehToman version computedAt activatedAt activatedBy archivedAt
  createdAt createdBy servicesPriced negativeMargins creditPrice creditCurrency
  fxSource basePolicyId
`;

const POLICY_DETAIL_FIELDS = `
  policy { ${POLICY_FIELDS} }
  prices {
    serviceKey labelFa labelEn unit basisCostUsd basisSource basisSample chargedRate
    costParseh computedParseh overrideParseh effectiveParseh marginPct activeParseh
    deltaParseh defaultParseh
  }
  walletParsehToman projectedBlendedMarginPct cheaper dearer negative
`;

// ── overview ──────────────────────────────────────────────────────────────────

export const OVERVIEW_QUERY = `
  query AiCostOverview($window: CostWindow!, $ownerType: String) {
    aiCostOverview(window: $window, ownerType: $ownerType) {
      totalCostUsd totalCostToman revenueParseh revenueUsd
      blendedCostPerParsehUsd blendedMarginPct invocations calls pricedShare
      daily { ${DAILY_FIELDS} }
      byService { key label costUsd sharePct count }
      byModel { key label costUsd sharePct count }
      alerts { kind severity count detail }
      activePolicyId activePolicyName fxTomanPerUsd parsehToman walletParsehToman
      mostExpensiveService creditCurrency
    }
  }
`;

// ── models ────────────────────────────────────────────────────────────────────

export const MODELS_QUERY = `
  query AiModels($window: CostWindow!, $includeDeprecated: Boolean) {
    aiModels(window: $window, includeDeprecated: $includeDeprecated) {
      modelId provider displayName modalities status notes createdAt updatedAt
      currentPrice { ${PRICE_FIELDS} }
      callsInWindow costUsdInWindow usedByServices
    }
  }
`;

export const MODEL_PRICES_QUERY = `
  query AiModelPrices($modelId: ID!) {
    aiModelPrices(modelId: $modelId) { ${PRICE_FIELDS} }
  }
`;

export const UPSERT_MODEL = `
  mutation UpsertAiModel($input: AiModelInput!) {
    upsertAiModel(input: $input) { modelId status }
  }
`;

export const ADD_MODEL_PRICE = `
  mutation AddAiModelPrice($input: AiModelPriceInput!) {
    addAiModelPrice(input: $input) {
      price { ${PRICE_FIELDS} }
      recomputedCalls recomputedInvocations
    }
  }
`;

// ── services ──────────────────────────────────────────────────────────────────

export const SERVICES_QUERY = `
  query AiServices($window: CostWindow!, $policyId: ID, $includeInactive: Boolean) {
    aiServices(window: $window, policyId: $policyId, includeInactive: $includeInactive) {
      services { ${SERVICE_ROW_FIELDS} }
      policyId fxTomanPerUsd parsehToman creditCurrency
    }
  }
`;

export const SERVICE_DETAIL_QUERY = `
  query AiServiceDetail($serviceKey: ID!, $window: CostWindow!, $policyId: ID) {
    aiServiceDetail(serviceKey: $serviceKey, window: $window, policyId: $policyId) {
      row { ${SERVICE_ROW_FIELDS} }
      models { modelId role calls avgInputTokens avgOutputTokens costUsd shareOfCostPct shareOfCallsPct unpriced }
      steps { step callsPerInvocation avgInputTokens avgOutputTokens costUsd shareOfCostPct }
      declaredRoles { role stepHint expectedCalls observed }
      daily { ${DAILY_FIELDS} }
      pricing {
        basisCostUsd basisSource basisSample fxTomanPerUsd parsehToman
        computedParseh overrideParseh effectiveParseh marginPct policyId policyName
        creditPrice creditCurrency
      }
      billedMinutes audioMinutesSent
    }
  }
`;

// ── ledger ────────────────────────────────────────────────────────────────────

export const INVOCATIONS_QUERY = `
  query AiInvocations($filter: AiInvocationFilter!, $page: Int!, $pageSize: Int!) {
    aiInvocations(filter: $filter, page: $page, pageSize: $pageSize) {
      invocations { ${INVOCATION_FIELDS} }
      total page pageSize
    }
  }
`;

export const INVOCATION_QUERY = `
  query AiInvocation($requestId: ID!) {
    aiInvocation(requestId: $requestId) {
      invocation { ${INVOCATION_FIELDS} }
      calls {
        id step role provider modelId inputTokens outputTokens cachedTokens audioSeconds
        tokensEstimated priceId costUsd providerCostUsd latencyMs status calledAt
      }
    }
  }
`;

/** The two option lists the ledger's filters need, in one round trip. */
export const LEDGER_OPTIONS_QUERY = `
  query AiLedgerOptions($window: CostWindow!) {
    aiServices(window: $window, includeInactive: true) {
      services { serviceKey labelFa labelEn }
    }
    aiModels(window: $window, includeDeprecated: true) { modelId }
  }
`;

// ── policies ──────────────────────────────────────────────────────────────────

export const POLICIES_QUERY = `
  query AiPricingPolicies {
    aiPricingPolicies { ${POLICY_FIELDS} }
  }
`;

export const POLICY_QUERY = `
  query AiPricingPolicy($id: ID!) {
    aiPricingPolicy(id: $id) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const CREDIT_PRICING_QUERY = `
  query CreditPricing {
    creditPricing { parsehToman creditPrice { amountMinor currency } }
  }
`;

export const EVENTS_QUERY = `
  query AiPricingEvents($page: Int!, $pageSize: Int!) {
    aiPricingEvents(page: $page, pageSize: $pageSize) {
      events { id at actorSub kind entity entityId before after }
      total
    }
  }
`;

export const SAVE_POLICY_DRAFT = `
  mutation SaveAiPricingPolicyDraft($id: ID, $cloneFromId: ID, $input: AiPricingPolicyInput!, $expectedVersion: Int) {
    saveAiPricingPolicyDraft(id: $id, cloneFromId: $cloneFromId, input: $input, expectedVersion: $expectedVersion) {
      ${POLICY_DETAIL_FIELDS}
    }
  }
`;

export const SIMULATE_POLICY = `
  mutation SimulateAiPricingPolicy($id: ID, $input: AiPricingPolicyInput) {
    simulateAiPricingPolicy(id: $id, input: $input) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const SET_SERVICE_OVERRIDE = `
  mutation SetAiServiceOverride($policyId: ID!, $serviceKey: ID!, $parseh: String, $expectedVersion: Int!) {
    setAiServiceOverride(policyId: $policyId, serviceKey: $serviceKey, parseh: $parseh, expectedVersion: $expectedVersion) {
      ${POLICY_DETAIL_FIELDS}
    }
  }
`;

export const ACTIVATE_POLICY = `
  mutation ActivateAiPricingPolicy($id: ID!, $expectedVersion: Int!) {
    activateAiPricingPolicy(id: $id, expectedVersion: $expectedVersion) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const ARCHIVE_POLICY = `
  mutation ArchiveAiPricingPolicy($id: ID!) {
    archiveAiPricingPolicy(id: $id) { ${POLICY_DETAIL_FIELDS} }
  }
`;

// ── the nightly dollar (PM-0427) ─────────────────────────────────────────────

export const FX_RATES_QUERY = `
  query AiFxRates($page: Int!, $pageSize: Int!) {
    aiFxRates(page: $page, pageSize: $pageSize) {
      rates {
        id rateDate fetchedAt source quotedAt marketTomanPerUsd premiumPct fxTomanPerUsd
        status reason outcome policyId
      }
      total page pageSize
    }
  }
`;

/** Read-only on the service; a mutation because it is a deliberate act (it reads the market). */
export const SIMULATE_NIGHTLY_FX = `
  mutation SimulateAiNightlyFx($policyId: ID, $marketToman: String) {
    simulateAiNightlyFx(policyId: $policyId, marketToman: $marketToman) {
      outcome status reason marketToman source quotedAt premiumPct fxToman currentFxToman
      baseFxToman policyId basePolicyId standIn repriceEnabled minChangePct maxStepPct whyHeld
      runAt
      changes { serviceKey unit fromParseh toParseh changePct marginPct tooLarge newlyNegative }
    }
  }
`;

/** Service labels for the simulation's price changes, which carry keys only. */
export const SERVICE_LABELS_QUERY = `
  query AiServiceLabels($window: CostWindow!) {
    aiServices(window: $window, includeInactive: true) {
      services { serviceKey labelFa labelEn }
    }
  }
`;

// ── visits (PM-0427) ──────────────────────────────────────────────────────────
// The operation names keep their «(»: fixtures route by `query.includes(name)`,
// and «query AiVisit» alone would also match «query AiVisits».

const VISIT_MONEY_FIELDS = 'policyId policyName policyStatus fxTomanPerUsd parsehToman creditPrice creditCurrency';

const VISIT_ROW_FIELDS = `
  appointmentId firstAt lastAt ownerType ownerId owners billedMinutes recordings runs
  chargedRuns earnedParseh earnedUsd paidUsd providerPaidUsd marginPct unpricedRuns
`;

const VISIT_FEATURE_FIELDS = `
  key labelFa labelEn group visitsUsing shareOfVisits runsPerVisit chargedRunsPerVisit
  earnedParsehPerVisit earnedUsdPerVisit paidUsdPerVisit marginPct pricedShare unpricedRuns
`;

export const VISIT_ECONOMICS_QUERY = `
  query AiVisitEconomics($window: CostWindow!, $filter: AiVisitFilter, $policyId: ID) {
    aiVisitEconomics(window: $window, filter: $filter, policyId: $policyId) {
      stats {
        visits recordedVisits runsPerVisit chargedRunsPerVisit recordingsPerVisit meanBilledMinutes
        totalEarnedParseh meanEarnedParseh p50EarnedParseh p90EarnedParseh meanEarnedUsd
        totalPaidUsd meanPaidUsd p50PaidUsd p90PaidUsd pricedVisits meanProviderPaidUsd
        marginPct pricedShare unpricedRuns
      }
      groups {
        total { ${VISIT_FEATURE_FIELDS} }
        services { ${VISIT_FEATURE_FIELDS} }
      }
      byMinutes {
        bucket minMinutes maxMinutes visits meanEarnedParseh meanEarnedUsd meanPaidUsd marginPct
        pricedVisits
      }
      unattributed {
        serviceKey labelFa labelEn group runs withoutIdRuns chargedRuns earnedParseh earnedUsd
        paidUsd unpricedRuns perVisitEarnedParseh perVisitPaidUsd
      }
      money { ${VISIT_MONEY_FIELDS} }
    }
  }
`;

export const VISITS_QUERY = `
  query AiVisits($window: CostWindow!, $filter: AiVisitFilter, $policyId: ID, $page: Int!, $pageSize: Int!, $sort: String) {
    aiVisits(window: $window, filter: $filter, policyId: $policyId, page: $page, pageSize: $pageSize, sort: $sort) {
      visits { ${VISIT_ROW_FIELDS} }
      total page pageSize
      money { ${VISIT_MONEY_FIELDS} }
    }
  }
`;

export const VISIT_QUERY = `
  query AiVisit($appointmentId: ID!, $policyId: ID) {
    aiVisit(appointmentId: $appointmentId, policyId: $policyId) {
      visit { ${VISIT_ROW_FIELDS} }
      lines {
        serviceKey labelFa labelEn group runs chargedRuns earnedParseh earnedUsd paidUsd
        providerPaidUsd unpricedRuns billedMinutes
      }
      invocations { ${INVOCATION_FIELDS} }
      money { ${VISIT_MONEY_FIELDS} }
      invocationsTruncated
    }
  }
`;

/** The visits view's two option lists — services and policies — in one round trip. */
export const VISIT_OPTIONS_QUERY = `
  query AiVisitOptions($window: CostWindow!) {
    aiServices(window: $window, includeInactive: true) {
      services { serviceKey labelFa labelEn }
    }
    aiPricingPolicies { id name status fxSource }
  }
`;
